<template>
    <div class="video-thumbnail relative group cursor-pointer overflow-hidden rounded-lg" @click="handleClick">
        <!-- Video Preview -->
        <video :src="videoSrc" :poster="poster"
            class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" muted
            preload="metadata" @loadeddata="onVideoLoaded" @error="onVideoError" />

        <!-- Play Button Overlay -->
        <div
            class="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
            <div
                class="bg-slate-800 rounded-full p-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <i class="fas fa-play text-primary-600 dark:text-primary-400 text-xl ml-1"></i>
            </div>
        </div>

        <!-- Duration Badge -->
        <div v-if="duration" class="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
            {{ formatDuration(duration) }}
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>

        <!-- Error State -->
        <div v-if="hasError" class="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800">
            <div class="text-center">
                <i class="fas fa-exclamation-triangle text-2xl text-red-500 mb-2"></i>
                <p class="text-xs text-slate-600 dark:text-slate-400">Video tidak dapat dimuat</p>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useVideoModal } from '../plugins/videoModal'

export default {
    name: 'VideoThumbnail',
    props: {
        videoSrc: {
            type: String,
            required: true
        },
        poster: {
            type: String,
            default: ''
        },
        title: {
            type: String,
            default: ''
        },
        description: {
            type: String,
            default: ''
        },
        autoPlay: {
            type: Boolean,
            default: false
        }
    },
    emits: ['click'],
    setup(props, { emit }) {
        const isLoading = ref(true)
        const hasError = ref(false)
        const duration = ref(null)
        const { openVideoModal } = useVideoModal()

        const onVideoLoaded = (event) => {
            isLoading.value = false
            hasError.value = false
            duration.value = event.target.duration

            // Auto play jika diaktifkan
            if (props.autoPlay) {
                event.target.play()
            }
        }

        const onVideoError = () => {
            isLoading.value = false
            hasError.value = true
        }

        const formatDuration = (seconds) => {
            if (!seconds || isNaN(seconds)) return ''

            const hours = Math.floor(seconds / 3600)
            const minutes = Math.floor((seconds % 3600) / 60)
            const secs = Math.floor(seconds % 60)

            if (hours > 0) {
                return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
            } else {
                return `${minutes}:${secs.toString().padStart(2, '0')}`
            }
        }

        const handleClick = () => {
            if (!hasError.value) {
                openVideoModal({
                    videoSrc: props.videoSrc,
                    title: props.title,
                    description: props.description
                })
                emit('click')
            }
        }

        return {
            isLoading,
            hasError,
            duration,
            onVideoLoaded,
            onVideoError,
            formatDuration,
            handleClick
        }
    }
}
</script>

<style scoped>
.video-thumbnail {
    aspect-ratio: 16 / 9;
}

.video-thumbnail video {
    pointer-events: none;
    background: transparent !important;
    filter: none !important;
    -webkit-filter: none !important;
    opacity: 1 !important;
}

/* Memastikan video di thumbnail tidak terpengaruh oleh styling yang membuat gelap */
.video-thumbnail video::-webkit-media-controls {
    display: none !important;
}

.video-thumbnail video::-webkit-media-controls-panel {
    display: none !important;
}

/* Override any potential dark mode effects */
.dark .video-thumbnail video {
    background: transparent !important;
    filter: none !important;
    -webkit-filter: none !important;
    opacity: 1 !important;
}
</style>